import React from 'react';
import { motion } from 'framer-motion';
import { Database, Calendar, CheckSquare, Type, Hash, Clock } from 'lucide-react';
import type { DataSourceSummary } from '../../services/modeler.service';

interface DataSourceListProps {
  datasources: DataSourceSummary[];
  selectedIds: (string | number)[]; // Cambio a array para soportar múltiples selecciones
  onToggle: (id: string | number) => void; // Cambio de onSelect a onToggle
  loading?: boolean;
}

const getTypeIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'google_sheets':
      return '📊';
    case 'csv':
      return '📄';
    case 'database':
      return '💾';
    default:
      return '📁';
  }
};

export const DataSourceList: React.FC<DataSourceListProps> = ({
  datasources,
  selectedIds,
  onToggle,
  loading = false,
}) => {
  return (
    <div className="h-full flex flex-col bg-slate-900/50 backdrop-blur-xl border-r border-slate-700/50">
      {/* Header */}
      <div className="p-4 border-b border-slate-700/50">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Database className="w-5 h-5 text-sky-400" />
          Data Sources
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          {selectedIds.length > 0 ? `${selectedIds.length} seleccionada(s) • ` : ''}
          {datasources.length} disponible{datasources.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {loading ? (
          <div className="p-4 text-center text-slate-400">
            <div className="animate-spin w-6 h-6 border-2 border-sky-400 border-t-transparent rounded-full mx-auto mb-2" />
            Loading...
          </div>
        ) : datasources.length === 0 ? (
          <div className="p-4 text-center text-slate-400">
            No data sources found
          </div>
        ) : (
          datasources.map((ds, index) => {
            const isSelected = selectedIds.includes(ds.id);
            
            return (
              <motion.div
                key={ds.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => onToggle(ds.id)}
                className={`
                  w-full p-3 rounded-lg text-left transition-all cursor-pointer
                  ${
                    isSelected
                      ? 'bg-sky-500/20 border-sky-400/50 shadow-lg shadow-sky-500/10'
                      : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-700/50 hover:border-slate-600/50'
                  }
                  border backdrop-blur-sm
                `}
              >
                {/* Checkbox + Name and Icon */}
                <div className="flex items-start gap-3 mb-2">
                  {/* Checkbox */}
                  <div className="pt-0.5">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggle(ds.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded border-slate-600 text-sky-500 focus:ring-sky-500 focus:ring-offset-slate-900 cursor-pointer"
                    />
                  </div>
                  
                  <span className="text-2xl">{getTypeIcon(ds.type)}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white text-sm truncate">
                      {ds.name}
                    </h3>
                    <p className="text-xs text-slate-400 truncate mt-0.5">
                      {ds.description || 'No description'}
                    </p>
                  </div>
                </div>

              {/* Stats */}
              <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
                <span className="flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  {ds.records || 0} rows
                </span>
                <span className="flex items-center gap-1">
                  <Type className="w-3 h-3" />
                  {ds.fields || 0} cols
                </span>
              </div>

              {/* Status badges */}
              <div className="flex items-center gap-2 mt-2">
                {ds.status === 'ACTIVE' && (
                  <span className="px-2 py-0.5 bg-sky-500/20 text-sky-400 text-xs rounded-full">
                    Active
                  </span>
                )}
                {ds.hasSmartSchema && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                    🧠 Schema
                  </span>
                )}
                {ds.hasERModel && (
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                    🏗️ ER
                  </span>
                )}
              </div>

              {/* Last update */}
              {ds.lastUpdate && (
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-2">
                  <Clock className="w-3 h-3" />
                  {new Date(ds.lastUpdate).toLocaleDateString()}
                </div>
              )}
            </motion.div>
          );
        })
        )}
      </div>
    </div>
  );
};
